import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsClinicUsers']
const INVITATIONS_QUERY_KEY = ['SettingsInvitations']
const EMPTY_ARRAY = []

export const useSettingsClinicUsers = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/settings/clinic_users`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the clinic users")
      }
      return res.json()
    },
  })

  return { clinicUsers: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useUpdateClinicUser = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateClinicUser, ...rest } = useMutation({
    mutationFn: async ({ id, ...userData }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/clinic_users/${id}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ clinic_user: userData }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update user')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateClinicUser, ...rest }
}

export const useRevokeClinicUser = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: revokeClinicUser, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/clinic_users/${id}/revoke`,
        {
          method: 'PATCH',
          headers,
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not revoke user access')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { revokeClinicUser, ...rest }
}

export const useReinstateClinicUser = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: reinstateClinicUser, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/clinic_users/${id}/reinstate`,
        {
          method: 'PATCH',
          headers,
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not reinstate user access')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { reinstateClinicUser, ...rest }
}

export const useInviteClinicUser = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: inviteClinicUser, ...rest } = useMutation({
    mutationFn: async invitationData => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/clinic_users/invitations`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ invitation: invitationData }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not send invitation')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
      queryClient.invalidateQueries(INVITATIONS_QUERY_KEY)
    },
  })

  return { inviteClinicUser, ...rest }
}

export const useSettingsInvitations = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: INVITATIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/clinic_users/invitations`,
        {
          method: 'GET',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the invitations")
      }
      return res.json()
    },
  })

  return { invitations: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useUpdateInvitation = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateInvitation, ...rest } = useMutation({
    mutationFn: async ({ id, ...invitationData }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/clinic_users/invitations/${id}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ invitation: invitationData }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update invitation')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(INVITATIONS_QUERY_KEY)
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateInvitation, ...rest }
}

export const useDeleteInvitation = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteInvitation, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/clinic_users/invitations/${id}`,
        {
          method: 'DELETE',
          headers,
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not delete invitation')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(INVITATIONS_QUERY_KEY)
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { deleteInvitation, ...rest }
}

export const useResendInvitation = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: resendInvitation, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/clinic_users/invitations/${id}/resend`,
        {
          method: 'POST',
          headers,
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not resend invitation')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(INVITATIONS_QUERY_KEY)
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { resendInvitation, ...rest }
}
